interface ParsesTo<T> {
  safeParse: (values: unknown) => { success: boolean; data?: T };
}

/**
 * [MCV-1543] Recover the values from an exit flush whose write failed.
 *
 * `useFlushStepOnExit` runs from unmount cleanup, so the form that owned the
 * values is gone by the time the PATCH resolves. On failure the step machine
 * keeps them in `pendingDraft` (see step-machine/types.ts) and this reads them
 * back when the user returns to the step, instead of hydrating from a
 * `listing.detail` the failed write never updated.
 *
 * Two guards, because the step actor outlives any single form:
 *
 * - The draft is re-validated with the caller's own schema. The machine is
 *   shared by every wizard step and stores the draft as `unknown`, so this is
 *   what makes reading it type-safe rather than assumed.
 * - The draft must belong to the record now on screen. Without the id check a
 *   draft left over from one listing would repopulate the form of the next one
 *   opened in the same session.
 */
export const resolveRetainedDraft = <T>(
  pendingDraft: unknown,
  schema: ParsesTo<T>,
  detailId?: number
): T | undefined => {
  if (pendingDraft === undefined || pendingDraft === null || detailId === undefined) {
    return undefined;
  }
  const parsed = schema.safeParse(pendingDraft);
  if (!parsed.success || parsed.data === undefined) return undefined;
  return (parsed.data as { id?: number }).id === detailId ? parsed.data : undefined;
};
